import { api } from '@/api';
import { ActionContext } from 'vuex';
import { IUserProfileCreate, IUserProfileUpdate } from '@/interfaces';
import { State } from '../state';
import { AdminState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetUser, commitSetCompanyReports, commitSetUsers } from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';
import { toBoolean } from '@/utils';
import { designMode } from '@/env';
import { AdminReportData } from '@/utils/data/adminReports';
import { IFinanceResponce, IFinancialInfoMaterialsResponse, IFundraisingReport, IFundraisingResponce, IProductResponce } from '../report';
import { commitSetFinance, commitSetFinancialInfoMaterials, commitSetFundraisings, commitSetProduct } from '../report/mutations';
import { IAccountInfo } from '../user';

type AdminContext = ActionContext<AdminState, State>;

export const actions = {
    async actionGetUsers(context: AdminContext) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                const adminReportData = new AdminReportData();
                data = adminReportData.getAdminUsers();
            } else {
                const response = await api.getUsers(context.rootState.main.token);
                data = response.data;
            }
            if (data) {
                commitSetUsers(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateUser(context: AdminContext, payload: { id: number, user: IUserProfileUpdate }) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateUser(context.rootState.main.token, payload.id, payload.user),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUser(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'User successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateUser(context: AdminContext, payload: IUserProfileCreate) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.createUser(context.rootState.main.token, payload),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetUser(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'User successfully created', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionGetReports(context: AdminContext) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                const adminReportData = new AdminReportData();
                data = adminReportData.getAdminReports();
            } else {
                const response = await api.getAdminReports(context.rootState.main.token);
                data = response.data;
            }
            if (data) {
                commitSetCompanyReports(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAdminGetLatestProduct(context: AdminContext, payload: number) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IProductResponce = {
                    id: 0,
                    company_id: 0,
                    name: 'CFOAi Products',
                    url: 'cfoai-product.com',
                    description: 'プロダクトやサービス概要が入ります。プロダクトやサービス概要が入ります。プロダクトやサービス概要が入ります。',
                    materials: [{
                        id: 1,
                        file_size: 100,
                        file_name: '株式会社会社CFO.Ai事業説明資料.pptx',
                        mime_type: 'application/vnd.ms-powerpoint',
                        created_at: '',
                        created_by: { id: 0 },
                        updated_at: '',
                        updated_by: { id: 0 },
                    }],
                    created_at: '',
                    created_by: {
                        id: 0
                    },
                    updated_at: '',
                    updated_by: {
                        id: 0
                    }
                };
                data = dummy;
            } else {
                const response = await api.getAdminProductReport(context.rootState.main.token, payload);
                data = response.data;
            }
            if (data) {
                commitSetProduct(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAdminGetLatestFinance(context: AdminContext, payload: number) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IFinanceResponce = {
                    id: 0,
                    company_id: 0,
                    expect_finance_date: new Date(),
                    expect_finance_amount: 0,
                    expect_finance_method: ["debt", "equity"],
                    expect_valuation_pre: 0,
                    achievement: '金融機関・投資家にアピールしたい実績があれば、教えてください',
                    use_of_funds: ["人件費", "外注費・業務委託費"],
                    materials: [{
                        id: 1,
                        file_size: 100,
                        file_name: '株式会社会社CFO.Ai事業説明資料.pptx',
                        mime_type: 'application/vnd.ms-powerpoint',
                        created_at: '',
                        created_by: { id: 0 },
                        updated_at: '',
                        updated_by: { id: 0 },
                    }],
                    created_at: '',
                    created_by: {
                        id: 0
                    },
                    updated_at: '',
                    updated_by: {
                        id: 0
                    },
                };
                data = dummy;
            } else {
                const response = await api.getAdminFinanceReport(context.rootState.main.token, payload);
                data = response.data;
            }
            if (data) {
                commitSetFinance(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAdminGetFinancialinfo(context: AdminContext, payload: number) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IFinancialInfoMaterialsResponse = [{
                    id: 1,
                    company_id: 1,
                    kind: 0,
                    file_size: 100,
                    file_name: '株式会社会社CFO.Ai事業説明資料.pptx',
                    mime_type: 'application/vnd.ms-powerpoint',
                    created_at: '',
                    created_by: { id: 0 },
                    updated_at: '',
                    updated_by: { id: 0 },
                }];
                data = dummy;
            } else {
                const response = await api.getAdminFinancialInfoMaterialsReport(context.rootState.main.token, payload);
                data = response.data;
            }
            if (data) {
                commitSetFinancialInfoMaterials(context, data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAdminGetFundraising(context: AdminContext, payload: number) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IFundraisingResponce = {
                    fundraised: true,
                    fundraising_json: '[{"id": 1, "method": "debt", "from_name": "test", "amount": 100, "payment_date": "2022-07-28", "repayment_date": "2022-07-28", "rescheduled": true, "repayment_type": 1, "interest_payment_type": 0, "valuation_post": null }]'
                };
                data = dummy;
            } else {
                const response = await api.getAdminFundraisingReport(context.rootState.main.token, payload);
                data = response.data;
            }
            if (data) {
                let obj = JSON.parse(data.fundraising_json);
                let fundraisings = obj
                for (let i = 0; i < obj.length; i++) {
                    if (obj[i].payment_date) {
                        fundraisings[i].payment_date = new Date(obj[i].payment_date)
                    }
                    if (obj[i].repayment_date) {
                        fundraisings[i].repayment_date = new Date(obj[i].repayment_date)
                    }
                }
                let commitData: IFundraisingReport = {
                    fundraised: data.fundraised,
                    fundraisings: obj,
                    fundraisingDeleteIds: [],
                };
                commitSetFundraisings(context, commitData);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCompleteReport(context: AdminContext, payload: number) {
        try {
            if (!toBoolean(designMode)) {
                await Promise.all([
                    api.completeReport(context.rootState.main.token, payload),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ])
                commitAddNotification(context, { content: '完了通知を送信しました', condition: 'saved' });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionReenterRequestReport(context: AdminContext, payload: { report_id: number, request: string }) {
        try {
            if (!toBoolean(designMode)) {
                await Promise.all([
                    api.reenterRequestReport(context.rootState.main.token, payload.report_id, payload.request),
                    await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
                ])
                commitAddNotification(context, { content: '再入力依頼を送信しました', condition: 'saved' });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionAdminGetAccountInfo(context: AdminContext, payload: number) {
        try {
            let data: any;
            if (toBoolean(designMode)) {
                let dummy: IAccountInfo = {
                    company_name: '株式会社 CFOAi',
                    company_url: 'cfo-ai.co.jp',
                    company_foundation_date: new Date(),
                    company_industry_category1: 'パソコン',
                    company_industry_detail1: 'パソコンソフトウェア',
                    company_industry_category2: '素材・化学・エネルギー',
                    company_industry_detail2: '鉄鋼・金属・ガラス・土石・ゴム',
                    company_industry_category3: '',
                    company_industry_detail3: '',
                    company_growth_phase: 'シリーズA：累計調達3億未満',
                    user_last_name: '大谷',
                    user_first_name: '翔太',
                    user_title: '専務取締役',
                    user_email: 'test@gmail.com',
                };
                data = dummy;
            } else {
                const response = await api.getAdminAccountInfo(context.rootState.main.token, payload);
                data = response.data;
            }
            if (data) {
                return data;
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
};

const { dispatch } = getStoreAccessors<AdminState, State>('');

export const dispatchCreateUser = dispatch(actions.actionCreateUser);
export const dispatchGetUsers = dispatch(actions.actionGetUsers);
export const dispatchUpdateUser = dispatch(actions.actionUpdateUser);
export const dispatchAdminGetReports = dispatch(actions.actionGetReports);
export const dispatchAdminGetLatestProduct = dispatch(actions.actionAdminGetLatestProduct);
export const dispatchAdminGetLatestFinance = dispatch(actions.actionAdminGetLatestFinance);
export const dispatchAdminGetFinancialinfo = dispatch(actions.actionAdminGetFinancialinfo);
export const dispatchAdminGetFundraising = dispatch(actions.actionAdminGetFundraising);
export const dispatchCompletedReport = dispatch(actions.actionCompleteReport);
export const dispatchReenterRequestReport = dispatch(actions.actionReenterRequestReport);
export const dispatchAdminGetAccountInfo = dispatch(actions.actionAdminGetAccountInfo);
